import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useCallAnalytics } from "@/services/analytics.query";
import type { AnalyticsPreset, AnalyticsRange, CallAnalytics } from "@/services/analytics.service";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { AgentCombobox } from "@/components/Agents/AgentCombobox";
import { customRangeProblem } from "@/utils/analytics-range";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePickerRange } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Phone, DollarSign, Clock, TrendingUp, Loader, AlertCircle, RefreshCw } from "lucide-react";

// All numbers on this page come from the `analytics` edge function; the
// browser only picks filters and renders (see src/services/analytics.service.ts).

type RangeChoice = AnalyticsPreset | "custom";

const RANGE_OPTIONS: Array<{ value: RangeChoice; label: string }> = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range…" },
];

const CALL_TYPE_META: Record<string, { label: string; color: string }> = {
  inbound: { label: "Inbound", color: "#0088FE" },
  outbound: { label: "Outbound", color: "#00C49F" },
  web: { label: "Web", color: "#FFBB28" },
};

const STATUS_COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8",
  "#82ca9d", "#ffc658", "#8dd1e1", "#a4de6c", "#d0ed57",
];

const TOOLTIP_STYLE = { borderRadius: "8px", border: "1px solid #e2e8f0" };

const RANGE_PROBLEM_ID = "analytics-range-problem";

const formatMoney = (dollars: number) => dollars.toFixed(2);

const formatDuration = (secs: number) =>
  secs >= 60 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;

const formatDay = (isoDay: string) => format(parseISO(isoDay), "MMM d, yyyy");

/** Local calendar days of a picked range, in the same tz the request uses; null while half-picked. */
const toCustomRange = (r: DateRange | undefined): { from: string; to: string } | null =>
  r?.from && r.to ? { from: format(r.from, "yyyy-MM-dd"), to: format(r.to, "yyyy-MM-dd") } : null;

/** The window to chart: a preset, or a picked window with nothing wrong with it; else none. */
function windowToChart(
  choice: RangeChoice,
  picked: { from: string; to: string } | null,
  problem: string | null,
): AnalyticsRange | null {
  if (choice !== "custom") return choice;
  return picked && !problem ? picked : null;
}

export default function Analytics() {
  const { currentWorkspaceId } = useWorkspace();

  const [rangeChoice, setRangeChoice] = useState<RangeChoice>("30d");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [callTypeFilter, setCallTypeFilter] = useState<string>("all");
  const [agentFilter, setAgentFilter] = useState<string>("all");

  const picked = toCustomRange(customRange);
  // Said here rather than by a refused request.
  const rangeProblem = rangeChoice === "custom" && picked ? customRangeProblem(picked) : null;
  // While a custom window is half-picked or refused there is none: nothing is
  // asked for, and the numbers already on screen stay put.
  const range = windowToChart(rangeChoice, picked, rangeProblem);

  const { data: analytics, isPending, isFetching, isError, error, refetch } = useCallAnalytics(
    currentWorkspaceId,
    {
      range,
      callType: callTypeFilter === "all" ? null : callTypeFilter,
      agentId: agentFilter === "all" ? null : agentFilter,
    },
  );

  const callTypeData = useMemo(
    () =>
      (analytics?.call_types ?? [])
        .filter((t) => t.value > 0)
        .map((t) => ({
          name: CALL_TYPE_META[t.name]?.label ?? t.name,
          value: t.value,
          color: CALL_TYPE_META[t.name]?.color ?? "#8884d8",
        })),
    [analytics],
  );
  const statusData = analytics?.statuses ?? [];

  return (
    <div className="p-8 space-y-6 h-full overflow-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Track performance metrics and insights across all agents
        </p>
        {analytics && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
            <span>{describeRange(analytics.range)}</span>
            {isFetching && <Loader className="h-3 w-3 animate-spin" aria-label="Refreshing" />}
          </p>
        )}
      </div>

      {/* Filters — always mounted so a filter change never unmounts the page */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select value={rangeChoice} onValueChange={(v) => setRangeChoice(v as RangeChoice)}>
          <SelectTrigger aria-label="Time range">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {rangeChoice === "custom" && (
          <div className="space-y-1">
            <DatePickerRange
              value={customRange}
              onChange={setCustomRange}
              placeholder="Pick start and end dates"
              disabled={{ after: new Date() }}
              // Said under the calendar while it is open, where it is seen and announced;
              // the page sentence below is what the trigger is described by once it closes.
              footer={rangeProblem ? <p role="alert" className="px-4 pb-3 text-sm text-destructive">{rangeProblem}</p> : null}
              aria-describedby={rangeProblem ? RANGE_PROBLEM_ID : undefined}
              aria-invalid={rangeProblem ? true : undefined}
            />
            {rangeProblem ? <p id={RANGE_PROBLEM_ID} className="text-sm text-destructive">{rangeProblem}</p> : null}
          </div>
        )}

        <Select value={callTypeFilter} onValueChange={setCallTypeFilter}>
          <SelectTrigger aria-label="Call type">
            <SelectValue placeholder="Call type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="inbound">Inbound</SelectItem>
            <SelectItem value="outbound">Outbound</SelectItem>
            <SelectItem value="web">Web</SelectItem>
          </SelectContent>
        </Select>

        <AgentCombobox
          workspaceId={currentWorkspaceId || undefined}
          value={agentFilter === "all" ? null : agentFilter}
          onChange={(id) => setAgentFilter(id ?? "all")}
          placeholder="All agents"
          clearLabel="All agents"
          ariaLabel="Agent"
        />
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn't load analytics</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{error instanceof Error ? error.message : "Please try again."}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", isFetching && "animate-spin")} />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isPending && !analytics && !isError && (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
          <Loader className="w-8 h-8 animate-spin mb-4" />
          <p>Loading analytics...</p>
        </div>
      )}

      {analytics && (
        <div className={cn("space-y-6 transition-opacity", isFetching && "opacity-60")}>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Calls"
              icon={<Phone className="h-4 w-4 text-muted-foreground" />}
              value={analytics.total_calls.toLocaleString()}
              hint={`${analytics.connected_calls.toLocaleString()} connected (${Math.round(analytics.connect_rate * 100)}%)`}
            />
            <MetricCard
              title="Total Cost"
              icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
              value={`$${formatMoney(analytics.total_cost)}`}
              hint="Total spend in selected period"
            />
            <MetricCard
              title="Avg Duration"
              icon={<Clock className="h-4 w-4 text-muted-foreground" />}
              value={formatDuration(analytics.avg_duration)}
              hint="Across connected calls"
            />
            <MetricCard
              title="Cost Per Call"
              icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
              value={`$${formatMoney(analytics.cost_per_call)}`}
              hint="Average cost per call"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Calls per {analytics.range.bucket}</CardTitle>
              </CardHeader>
              <CardContent className="pl-2">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.series}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={TOOLTIP_STYLE}
                      labelFormatter={(_, payload) => bucketLabel(payload?.[0]?.payload?.key, analytics.range.bucket)}
                    />
                    <Legend />
                    <Bar dataKey="calls" fill="hsl(var(--primary))" name="Calls" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="connected" fill="#00C49F" name="Connected" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cost per {analytics.range.bucket}</CardTitle>
              </CardHeader>
              <CardContent className="pl-2">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.series}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis
                      stroke="#888888"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value) => [`$${formatMoney(Number(value))}`, "Cost"]}
                      labelFormatter={(_, payload) => bucketLabel(payload?.[0]?.payload?.key, analytics.range.bucket)}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cost"
                      stroke="hsl(var(--primary))"
                      name="Cost ($)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Call Type Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {callTypeData.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={callTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name} (${entry.value})`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {callTypeData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {statusData.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name} (${entry.value})`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function describeRange(r: CallAnalytics["range"]) {
  const span = r.from === r.to ? formatDay(r.from) : `${formatDay(r.from)} – ${formatDay(r.to)}`;
  return `${span} · ${r.days} ${r.days === 1 ? "day" : "days"} · ${r.bucket === "week" ? "weekly" : "daily"} buckets · ${r.tz}`;
}

function bucketLabel(key: unknown, bucket: "day" | "week") {
  if (typeof key !== "string") return "";
  return bucket === "week" ? `Week of ${formatDay(key)}` : formatDay(key);
}

function MetricCard({ title, icon, value, hint }: { title: string; icon: React.ReactNode; value: string; hint: string }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
      No calls in this period
    </div>
  );
}
